import { BlobResponse, IBooleanResponse } from "shared";
import { ComponentTypes } from "../const/componentTypes";
import { YandexListTypes } from "../const/yandexListTypes";
import { UniqueIdentifier } from "@dnd-kit/core";
import { externalFormProtectTypes } from "../const/externalFormProtectTypes";
import { BarcodeTypes } from "../const/barcodeTypes";
import type { ILegalListFilter } from "./legal_list_filter";

export interface ITemplate {
    id: number;
    name: string;
    group_id: number;
    description?: string | null;
    code: string;
    access_type: string;
    format: string;
}

export interface IHistoryRecord {
    code?: string;
    comment?: string;
    file_id?: number;
    file_name: string;
    group_name: string;
    description?: string | null;
    hist_end_date: string;
    hist_end_time: string;
    hist_id: number;
    hist_start_date: string;
    hist_start_time: string;
    name: string;
    object_version_number: number;
    template_id: number;
    text_id?: number;
    updated_by_login: string;
}

export interface IBankDetail {
    item: string;
    bankNameWhiteList: number[];
    bankNameDefaultValue?: number;
}

export interface ILegalBankDetail {
    /** ya_id (как в списке компаний шаблона) */
    id: string;
    yaName?: string;
    bankBlackListArray: string[];
    bankDefaultValue?: string;
}

export type IFileSource = "history" | "present";

export const formExpired = "EXPIRED";

export type ITemplateComponent = ITemplateTextComponent |
    ITemplateInputComponent |
    ITemplateProcureComponent |
    ITemplateMoneyComponent |
    ITemplateFileComponent |
    ITemplateDateInputComponent |
    ITemplateCheckboxComponent |
    ITemplateRadioComponent |
    ITemplateSelectComponent |
    ITemplateInputWithSelectComponent |
    ITemplateTableComponent |
    ITemplateMultilineComponent |
    ITemplateLanguageComponent |
    ITemplateCounterpartyComponent |
    ITemplateYandexComponent |
    ITemplateExternalFormComponent |
    ITemplateHrPersonComponent |
    ITemplateAutonumberComponent;

export type ITagSelectComponent = ITemplateInputComponent |
    ITemplateMoneyComponent |
    ITemplateFileComponent |
    ITemplateSaveToExistingTicketComponent |
    ITemplatePdfPasswordComponent |
    ITemplateDateInputComponent |
    ITemplateCheckboxComponent |
    ITemplateRadioComponent |
    ITemplateSelectComponent |
    ITemplateInputWithSelectComponent |
    ITemplateBarcodeComponent |
    ITemplateMultilineComponent;

export type IListComponent = ITemplateInputWithSelectComponent |
    ITemplateProcureComponent |
    ITemplateRadioComponent |
    ITemplateSelectComponent |
    ITemplateTableComponent;

export interface ITemplateTextComponent extends ICommonTemplateComponentProps {
}

export interface ITemplateLanguageComponent extends ICommonTemplateComponentProps {
    isLangEn: boolean;
}

export type MergeDocTypes = "doc" | "txt" | "zip";

export interface ITemplateMassGenerationComponent extends ICommonTemplateComponentProps {
    startrekQueue?: string;
    issueType?: string; //undefined means "task"
    mergeDoc?: MergeDocTypes;
    format?: "rtf" | "null"; //undefined means "pdf"
}

export interface ITemplateExternalFormComponent extends ICommonTemplateComponentProps {
}

export interface ITemplateUuidComponent extends ICommonTemplateComponentProps {
    name: string;
}

export type AutonumberCounterLoadError = "load-failed" | "not-found" | "forbidden" | "date-not-found";

export interface IAutonumberCounter {
    id: number;
    name: string;
    tag: string;
    mask?: string | null;
    code?: string | null;
    date_tag?: string | null;
    is_attr_used: boolean;
    login: string;
}

export interface IAutonumberCounterSaveRequest {
    id?: number;
    name: string;
    tag: string;
    mask: string;
    code?: string;
    date_tag?: string;
    is_attr_used: boolean;
}

export interface IAutonumberCounterFilters {
    id?: string;
    name?: string;
}

export interface ITemplateAutonumberComponent extends ICommonTemplateComponentProps {
    autonumberId?: number;
    counterName: string;
    name: string;
    mask: string;
    code?: string;
    dateComponentId?: UniqueIdentifier;
    dateTag?: string;
    isAttrUsed: boolean;
    initialCounter?: IAutonumberCounter;
    counterReadOnly?: boolean;
    counterLoadError?: AutonumberCounterLoadError;
}

export interface ITemplateSaveAndTicketComponent extends ICommonTemplateComponentProps {
    name: string;
    startrekQueue?: string;
    issueType?: string; //undefined means "task",
    isProtected?: boolean;
    isDocPdf?: boolean;//undefined means true
    isDocRtf?: boolean;//undefined means true
    isTicketRtf?: boolean;//undefined means true
    isTicketPdf?: boolean;//undefined means true
    isDocPdfNopass?: boolean;
    isDocRtfNopass?: boolean;
    isTicketPdfNopass?: boolean;
    isTicketRtfNopass?: boolean;
    isDocAndTicketPdf?: boolean;
    isDocAndTicketRtf?: boolean;
    isTicketPdfRtfNopass?: boolean;
    isUploadFile?: boolean;
}

export interface ITemplateTrackerIntegrationComponent extends ICommonTemplateComponentProps {
    name: string;
    startrekFields?: TicketField;
    textWithVariables?: string;
}

export interface ITemplateSaveToExistingTicketComponent extends ICommonTemplateComponentProps {
    name: string;
    startrekQueueAttach?: string;
    startrekCommentTemplate?: string;
    startrekCommentTemplate_en?: string;
    isDocPdf?: boolean; //undefined means true
    isDocRtf?: boolean; //undefined means true
    isDocPdfNopass?: boolean;
    isDocRtfNopass?: boolean;
    isUploadFile?: boolean;
    isFullView?: boolean; //undefined means true
}


export interface ITemplateProcureComponent extends ICommonTemplateComponentProps, IInputList {
    startrekQueueIproc?: string;
    ticketNum?: string;
    name: string;
    defaultValue?: string;
    additionalDefaultValue?: string;
    hideIprocStyle: boolean;
    isTemplate: boolean;
    format?: "rtf"; //undefined means "pdf"
}

export interface ITemplateBarcodeComponent extends ICommonTemplateComponentProps {
    name: string;
    barcodeType: BarcodeTypes;
    ratio?: string;
}

export interface ITemplateHrPersonComponent extends ICommonTemplateComponentProps, IFieldList {
    readonly?: boolean;
    limitToBusinessGroups?: number[];
}

export interface ITemplateCounterpartyComponent extends ICommonTemplateComponentProps, IFieldList {
    readonly?: boolean;
    selectonly?: boolean;
    limitToBusinessGroups?: number[];
}


export type TLegalCompaniesBlackListByKey = Record<string, string[]>;

export interface ITemplateYandexComponent extends ICommonTemplateComponentProps, IFieldList {
    readonly?: boolean;
    selectonly?: boolean;
    optionsList: YandexListTypes;
    defaultValue?: string;
    list?: string[];
    listBankDetails?: IBankDetail[];
    listLegalBankDetails?: ILegalBankDetail[];
    bgBlackList?: TLegalCompaniesBlackListByKey;
    buBlackList?: TLegalCompaniesBlackListByKey;
    countriesBlackList?: TLegalCompaniesBlackListByKey;
    additionalCompanies?: string[];
    bankDetailsFilter?: ILegalListFilter;
}

export interface ITemplateInputComponent extends ICommonTemplateComponentProps {
    name: string;
    isNumber?: boolean;
    minNumber?: string;
    maxNumber?: string;
    minStringLength?: string;
    maxStringLength?: string;
}

export interface ITemplateMultilineComponent extends ICommonTemplateComponentProps {
    name: string;
}

export interface ITemplatePdfPasswordComponent extends ICommonTemplateComponentProps {
    name: string;
}

export interface ITemplateMoneyComponent extends ICommonTemplateComponentProps {
    name: string;
    useEnValue?: boolean;
    minNumber?: string;
    maxNumber?: string;
}

export interface ITemplateCheckboxComponent extends IVisibilityTemplateComponentProps {
    name: string;
    defaultValue?: boolean;
    type: ComponentTypes;
    useWysiwygTooltip?: boolean; // false if not specified
    wysiwyg_tooltip?: string;
    wysiwyg_tooltip_en?: string;
    label: string;
    label_en?: string;
    id: UniqueIdentifier;
    tooltip?: string;
    tooltip_en?: string;
    isRowGroup?: boolean;
    readonly?: boolean; //false if not specified
    protect?: externalFormProtectTypes;
}

export interface ITemplateRadioComponent extends ICommonTemplateComponentProps, IInputList {
    name: string;
    defaultValue?: string;
    readonly?: boolean; //false if not specified
    direction?: string; // "row" by default
}

export interface ITemplateSelectComponent extends ICommonTemplateComponentProps, IInputList {
    name: string;
    defaultValue?: string;
    readonly?: boolean; //false if not specified
}

export interface ITemplateInputWithSelectComponent extends ICommonTemplateComponentProps, IInputList {
    name: string;
    defaultValue?: string;
    readonly?: boolean; //false if not specified
    selectonly?: boolean; //false if not specified
}

export interface ITemplateTableComponent extends IVisibilityTemplateComponentProps, IInputList {
    type: ComponentTypes;
    label: string;
    label_en?: string;
    id: UniqueIdentifier;
    useWysiwygTooltip?: boolean; // false if not specified
    wysiwyg_tooltip?: string;
    wysiwyg_tooltip_en?: string;
    tooltip?: string;
    tooltip_en?: string;
    name: string;
    drawHeader?: boolean; //true if not specified
    required?: boolean; //true if not specified
    readonly?: boolean; //false if not specified
    protect?: externalFormProtectTypes;
}

export interface ITemplateDateInputComponent extends ICommonTemplateComponentProps {
    name: string;
    readonly?: boolean; //false if not specified
    isDefaultDate?: boolean; //true if not specified
}

export interface ITemplateFileComponent extends ICommonTemplateComponentProps {
    name: string;
}

export interface ICommonTemplateComponentProps extends IVisibilityTemplateComponentProps {
    type: ComponentTypes;
    label?: string;
    name?: string;
    label_en?: string;
    id: UniqueIdentifier;
    useWysiwygTooltip?: boolean; // false if not specified
    wysiwyg_tooltip?: string;
    wysiwyg_tooltip_en?: string;
    tooltip?: string;
    tooltip_en?: string;
    isRowGroup?: boolean;
    required?: boolean; //true if not specified
    readonly?: boolean; //false if not specified
    protect?: externalFormProtectTypes;
}

export interface IVisibilityTemplateComponentProps {
    visibilityParentName?: string;
    visibilityParentNegate?: boolean;
    visibilityParentValue?: string | boolean;
}

export interface IInputList {
    list?: string[];
    list_en?: string[];
}

export interface IFieldList {
    suffix?: string;
    fieldsList?: string[];
}

export interface ITemplatesService {
    loadTemplates: (extUserId: string | null) => Promise<ITemplateListResponse>;
    loadTemplate: (id: number, extUserId: string | null) => Promise<ITemplateComponent[]>;
    loadPassword: (id: number, extUserId: string | null) => Promise<string | undefined>;
    getTemplate: (id: number) => Promise<IHistoryRecord>;
    saveTemplate: (id: number, text: string) => Promise<string>;
    loadAutonumberCounters: (filters?: IAutonumberCounterFilters) => Promise<IAutonumberCounter[]>;
    saveAutonumberCounter: (request: IAutonumberCounterSaveRequest) => Promise<number>;
    addTemplate: (group_id: number, code: string, name: string, description: string, userName: string | undefined, userNameEn: string | undefined) => Promise<number>;
    editTemplate: (id: number, group_id: number, code: string, name: string, description: string) => Promise<string>;
    copyTemplate: (id: number, group_id: number, code: string, name: string, description: string, userName: string | undefined, userNameEn: string | undefined) => Promise<string>;
    deleteTemplate: (id: number, group_id: number) => Promise<string>;
    downloadTemplate: (docId: number, xxcdoc_lang: string) => Promise<BlobResponse>;
    postComment: (hist_id: number, template_id: number, hist_end_date: string, comment: string, tzOffset: number) => Promise<IBooleanResponse>;
    loadHistory: (id: number, dateFrom: string, dateTo: string, limit?: number, tzOffset?: number) => Promise<IHistoryResponse>;
    downloadHistory: (template_id: number, version_id: number, source: IFileSource, date_time: string, xxcdoc_lang: string, format: string, tzOffset: number) => Promise<BlobResponse>;
    loadBusinessGroups: (extUserId: string | null) => Promise<IBusinessGroup[]>;
    loadOrganizations: (extUserId: string | null) => Promise<IOrganization[]>;
    getOrganizationDetails: (ya_name: string, extUserId: string | null) => Promise<IOrganization>;
    getPredefinedExternalData: (id: number, extUserId: string | null) => Promise<Record<string, any>>;
    getExtDefinedData: (id: number, extUserId: string | null) => Promise<Record<string, any>>;
    getExtProtectedData: (id: number, extUserId: string | null) => Promise<Record<string, any>>;
    setRecentlyEditedTemplate: (templateId: number) => Promise<number[]>;
    getRecentlyEditedTemplates: () => Promise<number[]>;
}

export interface IUploadFileResponse {
    extension: string;
    status: string;
}

export interface IBusinessGroup {
    id: number;
    name: string;
}

export interface IOrganization {
    "id": number,
    "ya_name": string;
    "ya_full_name": string;
    "ya_inn": string;
    "ya_address": string;
    "ya_address_eng": string;
    "ya_system_name": string;
    "ya_reg_num": string;
    "ya_org_id": number,
    "ya_bg_id": number,
    "ya_eng_name_full": string;
    "ya_eng_name_short": string;
    "ya_reg_date": string;
    "ya_kpp": string;
    "ya_kpp_main": string;
    "ya_oktmo": string;
    "ya_okpo": string;
    "ya_general_visa_login": string;
    "ya_general_visa_fio": string;
    "ya_general_visa_familyio": string;
    "ya_general_visa_iofamily": string;
    "ya_general_visa_gen": string;
    "ya_general_visa_dat": string;
    "ya_general_visa_acc": string;
    "ya_general_visa_inst": string;
    "ya_general_visa_prepos": string;
    "ya_general_visa_job": string;
    "ya_general_visa_job_gen": string;
    "ya_general_visa_job_dat": string;
    "ya_general_visa_job_acc": string;
    "ya_general_visa_job_inst": string;
    "ya_general_visa_job_prepos": string;
    "ya_general_visa_last_name": string;
    "ya_general_visa_first_name": string;
    "ya_general_visa_middle_names": string;
    "ya_general_visa_birth_date": string;
    "ya_general_visa_inn": string;
    "ya_general_visa_snils": string;
    "ya_general_visa_citizenship": string;
    "ya_org_country": string;
    "ya_org_region": string;
    "ya_org_area": string;
    "ya_org_town": string;
    "ya_org_settlement": string;
    "ya_org_street": string;
    "ya_org_house": string;
    "ya_org_building": string;
    "ya_org_apartment": string;
    "ya_org_postal_code": string;
    "ya_org_combination": string;
    "ya_it_num": string;
    "ya_it_start_date": string;
    ya_bank_details?: IOrgBankDetail[]
    "isDefault": string,
    "isRead": boolean;
}

export interface IOrgBankDetail {
    id: number;
    ya_bank_name: string;
    ya_bic: string;
    ya_corr_account: string;
    ya_payment_account: string;
}

export type IHistoryResponse = IHistoryRecord[];

export interface ITicketTypesResponse {
    issueTypes: IssueType[];
}

export interface IssueType {
    display: string;
    key: string;
}

export interface TicketField {
    id: string;
    name: string;
}

export interface IExternalUrlResponse {
    days?: number;
    health?: number;
    url?: string;
}

export interface ITemplateListResponse {
    expired: boolean;
    templateList: ITemplate[];
}
